import { Link } from 'react-router-dom';
import { ArrowRight, Star, ExternalLink } from 'lucide-react';

const Reviews = () => {
  const reviews = [
    {
      id: 1,
      slug: '888-casino-review',
      name: '888 Casino',
      url: 'https://www.888casino.com/',
      bonus: '200% up to £200 Bonus',
      rating: 4.5,
      description: 'One of the UK\'s most established operators delivers a premium experience with extensive game selection and reliable service.',
      bgGradient: 'from-purple-900/20 to-dark-black',
      borderColor: 'border-purple-500/30'
    },
    {
      id: 2,
      slug: 'kwiff-review',
      name: 'Kwiff',
      url: 'https://www.kwiff.com',
      bonus: '£20 on slots + 40 Free Spins',
      rating: 4.3,
      description: 'Innovative operator shaking up the market with unique bonus structures and player-friendly terms that actually deliver value.',
      bgGradient: 'from-blue-900/20 to-dark-black',
      borderColor: 'border-blue-500/30'
    },
    {
      id: 3,
      slug: 'plaza-royal-review',
      name: 'Plaza Royal',
      url: 'https://www.plazaroyal.com',
      bonus: '100% up to £50 + 20 Spins',
      rating: 4.2,
      description: 'Solid operator offering reliable gameplay, fair bonus terms, and consistent service that keeps players coming back.',
      bgGradient: 'from-green-900/20 to-dark-black',
      borderColor: 'border-green-500/30'
    }
  ];

  return (
    <main className="min-h-screen py-20 bg-dark-black">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              UK Casino Reviews
            </h1>
            <p className="text-gray-400 text-xl max-w-3xl mx-auto">
              In-depth reviews of the UK's top online casinos. We test games, bonuses, payments, and customer service so you don't have to.
            </p>
          </div>

          {/* Reviews Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {reviews.map((review, index) => (
              <article
                key={review.id}
                itemScope
                itemType="https://schema.org/Review"
                className={`bg-gradient-to-br ${review.bgGradient} border-2 ${review.borderColor} rounded-xl p-8 hover:border-toxic-orange transition-all duration-300 transform hover:-translate-y-2 group`}
              >
                <Link
                  to={`/reviews/${review.slug}`}
                  data-discover="true"
                  className="block"
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="relative w-16 h-16 bg-gray-800 rounded-lg flex items-center justify-center border border-gray-700 overflow-hidden">
                        <img 
                          src={`/images/${index === 0 ? 'kenny-eliason-VOHZuZ8U4Lg-unsplash.webp' : index === 1 ? 'james-nilsson-Ih32pz1tMis-unsplash.webp' : 'jaroslaw-kwoczala-nlor0fa24xI-unsplash.webp'}`} 
                          alt={`${review.name}`} 
                          className="w-full h-full object-cover opacity-30"
                          itemProp="image"
                        />
                        <span className="absolute text-toxic-orange font-bold text-xl">
                          {review.name.charAt(0)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1" itemScope itemType="https://schema.org/Rating">
                      <Star className="text-toxic-orange fill-toxic-orange" size={20} />
                      <span className="text-white font-semibold" itemProp="ratingValue">{review.rating}</span>
                      <meta itemProp="bestRating" content="5" />
                    </div>
                  </div>

                  <div itemScope itemType="https://schema.org/Organization">
                    <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-toxic-orange transition-colors" itemProp="name">
                      {review.name}
                    </h3>
                  </div>
                  
                  <div className="mb-4">
                    <span className="text-toxic-orange font-semibold text-lg block mb-2">
                      {review.bonus}
                    </span>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4" itemProp="reviewBody">
                      {review.description}
                    </p>
                  </div>

                  <div className="inline-flex items-center text-toxic-orange font-semibold group-hover:text-orange-400 transition-colors">
                    Read Full Review
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                  </div>
                </Link>
              </article>
            ))}
          </div>

          {/* How We Review Section */}
          <div className="bg-gradient-to-br from-gray-900 to-dark-black border border-gray-800 rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-white mb-6">
              How We Review Casinos
            </h2>
            
            <div className="space-y-6 text-gray-300 leading-relaxed">
              <p>
                Our casino reviews aren't marketing copy or paid promotions. Every review comes from hands-on testing, real account creation, actual gameplay, and genuine withdrawal attempts. We don't just read websites or trust operator claims—we test everything ourselves to give you accurate, actionable information.
              </p>
              
              <p>
                We start with licensing and security. Every casino we review must hold a valid UK Gambling Commission license. We verify license numbers, check regulatory standing, and confirm that operators meet UKGC requirements. Security is tested through account creation, payment processing, and data handling. We look for SSL encryption, secure payment gateways, and transparent privacy policies. If a casino doesn't meet these fundamental requirements, it doesn't get reviewed.
              </p>
              
              <p>
                Game selection is tested through actual play. We don't just count games or list providers—we play them. We test loading times, mobile compatibility, RTP availability, and game quality. We check whether advertised games are actually available, whether demo modes work, and whether games perform as expected. A casino might claim 500 slots, but if 200 are duplicates or unavailable, that matters.
              </p>
              
              <p>
                Bonuses are tested through actual claims. We create accounts, make deposits, claim bonuses, and attempt to clear wagering requirements. We read every term, calculate real bonus value, and test whether terms are actually enforced. If a casino advertises a £500 bonus but the wagering requirements make it nearly impossible to clear, we explain why. If bonus terms are fair and achievable, we highlight that too.
              </p>
              
              <p>
                Payment methods are tested through real transactions. We deposit using various methods, test withdrawal speeds, and verify processing times. We check for hidden fees, minimum amounts, and maximum limits. We test whether advertised withdrawal speeds match reality. If a casino claims 24-hour withdrawals but takes 5 days, we report that discrepancy.
              </p>
              
              <p>
                Customer support is tested through real inquiries. We contact support via live chat, email, and phone (when available) to assess response times, answer quality, and problem resolution. We ask actual questions players might have, not scripted queries. We test support during peak hours and off-peak times to see how consistency varies. Support quality directly impacts your experience, so we take this seriously.
              </p>
              
              <p>
                Mobile experience is tested on actual devices. We don't just check if a casino has a mobile site—we test it on various smartphones and tablets. We check game compatibility, interface usability, payment processing, and overall performance. Mobile play is how most players access casinos, so mobile experience significantly affects our ratings.
              </p>
              
              <p>
                Responsible gambling tools are evaluated for accessibility and effectiveness. We check whether tools are easy to find, simple to use, and actually work as advertised. We test deposit limits, loss limits, session time limits, and self-exclusion options. A casino might offer these tools, but if they're buried in settings or don't function properly, that affects our assessment.
              </p>
              
              <p>
                Our ratings reflect real performance, not marketing claims. A casino might advertise "fastest withdrawals" or "best bonuses," but if our testing doesn't support those claims, our review reflects reality. We're honest about strengths and weaknesses, highlighting what casinos do well while being transparent about areas where they fall short.
              </p>
              
              <p>
                We update reviews regularly because casinos change. Withdrawal speeds improve or worsen. Bonus terms shift. Game selections expand or contract. Customer support quality varies. What was accurate six months ago might be outdated today. We revisit casinos periodically to ensure our reviews reflect current performance.
              </p>
              
              <p>
                Our goal is simple: give you the information you need to make smart choices. We test what matters to players: game quality, bonus fairness, payment speed, support responsiveness, and overall reliability. We don't waste your time with fluff or marketing speak. Every review provides actionable insights that help you choose casinos that match your priorities and playing style.
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Reviews;

